import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import {
  hasNativeTracker, ensureNativeTracker, trackerStatus, askLocation, askBackground,
  askNotifications, askBatteryExemption, openAppSettings,
} from '../lib/nativeTracker';
import { Button } from './ui/button';
import { MapPinOff, BellOff, BatteryWarning } from 'lucide-react';

export const CONSENT_KEY = 'sahakar_presence_consent';
export const CONSENT_EVENT = 'sahakar-presence-consent';
const BATTERY_ASKED_KEY = 'sahakar_battery_asked';

// Starts the native background service and shows what phone settings are still missing.
export default function NativeTrackerSetup() {
  const { user } = useAuth();
  const enabled = hasNativeTracker() && !!user && user.role !== 'tv_display' && !user.impersonated_by;
  const tracked = enabled && user.role !== 'super_admin';
  const userId = user?.id;
  const [consented, setConsented] = useState(() => localStorage.getItem(CONSENT_KEY) === '1');
  const [status, setStatus] = useState(null);
  const ranFor = useRef(null);

  useEffect(() => {
    const on = () => setConsented(true);
    window.addEventListener(CONSENT_EVENT, on);
    return () => window.removeEventListener(CONSENT_EVENT, on);
  }, []);

  const setup = useCallback(async () => {
    try {
      if (tracked) await askLocation();
      await askNotifications();
      if (tracked) await askBackground();
      let st = await ensureNativeTracker(userId);
      if (tracked && localStorage.getItem(BATTERY_ASKED_KEY) !== '1' && !st.battery) {
        localStorage.setItem(BATTERY_ASKED_KEY, '1');
        st = await askBatteryExemption();
      }
      setStatus(st);
    } catch (e) { console.debug('native tracker setup failed', e); }
  }, [tracked, userId]);

  useEffect(() => {
    if (!enabled || (tracked && !consented) || ranFor.current === userId) return;
    ranFor.current = userId;
    setup();
  }, [enabled, tracked, consented, userId, setup]);

  // Re-check after the user returns from phone Settings.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVis = () => { if (document.visibilityState === 'visible') trackerStatus().then(setStatus).catch(() => {}); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [enabled]);

  if (!enabled || !status) return null;
  const issues = [
    tracked && !status.background && { key: 'bg', icon: MapPinOff, text: 'Set Location to "Allow all the time" so tracking works when the app is closed.' },
    !status.notifications && { key: 'notif', icon: BellOff, text: 'Turn on Notifications so you receive alerts.' },
    tracked && !status.battery && { key: 'batt', icon: BatteryWarning, text: 'Set Battery to "Unrestricted" so the phone does not stop the app.' },
  ].filter(Boolean);
  if (!issues.length) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:max-w-sm z-50 rounded-xl border border-amber-500/40 bg-card shadow-2xl p-3 space-y-2" data-testid="native-setup-card">
      <div className="text-sm font-semibold">Finish phone setup</div>
      {issues.map(({ key, icon: Icon, text }) => (
        <div key={key} className="flex items-start gap-2 text-xs text-muted-foreground" data-testid={`native-setup-${key}`}>
          <Icon size={14} className="text-amber-500 shrink-0 mt-0.5"/>{text}
        </div>
      ))}
      <Button size="sm" className="w-full" onClick={() => (issues.some(i => i.key === 'batt') && issues.length === 1 ? askBatteryExemption() : openAppSettings())}
        data-testid="native-setup-open-settings">Open phone settings</Button>
    </div>
  );
}
